import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { confirmDialog } from "@/components/ui/app-dialogs";

/**
 * Job page: the "View your test reports" link a big packet sent (Corbin,
 * 10/21: it never expires, like the photo link, but the office can turn it
 * off from the job). Shows only while a link is on.
 */
export function ReportLinkStrip({ jobId, canManage }: { jobId: string; canManage: boolean }) {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: ["job", jobId, "report-link"],
        queryFn: () => api.get<{ on: boolean; url: string | null }>(`/api/jobs/${jobId}/report-link`),
        staleTime: 60_000,
    });
    const off = useMutation({
        mutationFn: () => api.post(`/api/jobs/${jobId}/report-link/off`, {}),
        onSuccess: () => {
            toast.success("Report link turned off. The old link no longer opens.");
            qc.invalidateQueries({ queryKey: ["job", jobId, "report-link"] });
        },
        onError: () => toast.error("Could not turn the link off. Try again."),
    });
    if (!q.data?.on) return null;
    const turnOff = async () => {
        const ok = await confirmDialog({
            title: "Turn off the report link?",
            message:
                "The customer's link to their test reports stops working. If you send the packet again, it gets a new link.",
            confirmLabel: "Turn off",
            destructive: true,
            focusCancel: true,
        });
        if (ok) off.mutate();
    };
    return (
        <div
            className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-[13px]"
            data-testid="report-link-strip"
        >
            <Link2 className="h-4 w-4 flex-none text-foreground/60" aria-hidden />
            <span className="min-w-0 flex-1">
                The customer has a link to their test reports.{" "}
                {q.data.url && (
                    <a href={q.data.url} target="_blank" rel="noreferrer" className="font-semibold underline">
                        Open it
                    </a>
                )}
            </span>
            {canManage && (
                <Button size="sm" variant="outline" disabled={off.isPending} onClick={() => void turnOff()}>
                    Turn off
                </Button>
            )}
        </div>
    );
}
